export interface SelectOptionItem {
  readonly value: string;
  readonly label: string;
}

/**
 * ISO 639-1 & BCP-47 language options for product link targeting (`hreflang`).
 */
export const LANGUAGE_OPTIONS: readonly SelectOptionItem[] = [
  { value: 'en', label: 'English (en)' },
  { value: 'en-GB', label: 'English — United Kingdom (en-GB)' },
  { value: 'en-US', label: 'English — United States (en-US)' },
  { value: 'en-IE', label: 'English — Ireland (en-IE)' },
  { value: 'en-AU', label: 'English — Australia (en-AU)' },
  { value: 'en-CA', label: 'English — Canada (en-CA)' },
  { value: 'fr', label: 'French (fr)' },
  { value: 'fr-FR', label: 'French — France (fr-FR)' },
  { value: 'fr-BE', label: 'French — Belgium (fr-BE)' },
  { value: 'fr-CH', label: 'French — Switzerland (fr-CH)' },
  { value: 'fr-CA', label: 'French — Canada (fr-CA)' },
  { value: 'de', label: 'German (de)' },
  { value: 'de-DE', label: 'German — Germany (de-DE)' },
  { value: 'de-AT', label: 'German — Austria (de-AT)' },
  { value: 'de-CH', label: 'German — Switzerland (de-CH)' },
  { value: 'es', label: 'Spanish (es)' },
  { value: 'es-ES', label: 'Spanish — Spain (es-ES)' },
  { value: 'es-MX', label: 'Spanish — Mexico (es-MX)' },
  { value: 'it', label: 'Italian (it)' },
  { value: 'nl', label: 'Dutch (nl)' },
  { value: 'nl-NL', label: 'Dutch — Netherlands (nl-NL)' },
  { value: 'nl-BE', label: 'Dutch — Belgium (nl-BE)' },
  { value: 'pt', label: 'Portuguese (pt)' },
  { value: 'pt-PT', label: 'Portuguese — Portugal (pt-PT)' },
  { value: 'pt-BR', label: 'Portuguese — Brazil (pt-BR)' },
  { value: 'pl', label: 'Polish (pl)' },
  { value: 'sv', label: 'Swedish (sv)' },
  { value: 'da', label: 'Danish (da)' },
  { value: 'no', label: 'Norwegian (no)' },
  { value: 'fi', label: 'Finnish (fi)' },
  { value: 'el', label: 'Greek (el)' },
  { value: 'cs', label: 'Czech (cs)' },
  { value: 'hu', label: 'Hungarian (hu)' },
  { value: 'ro', label: 'Romanian (ro)' },
  { value: 'bg', label: 'Bulgarian (bg)' },
  { value: 'hr', label: 'Croatian (hr)' },
  { value: 'sk', label: 'Slovak (sk)' },
  { value: 'sl', label: 'Slovenian (sl)' },
  { value: 'et', label: 'Estonian (et)' },
  { value: 'lv', label: 'Latvian (lv)' },
  { value: 'lt', label: 'Lithuanian (lt)' },
  { value: 'ga', label: 'Irish (ga)' },
  { value: 'cy', label: 'Welsh (cy)' },
  { value: 'mt', label: 'Maltese (mt)' },
  { value: 'is', label: 'Icelandic (is)' },
  { value: 'tr', label: 'Turkish (tr)' },
  { value: 'uk', label: 'Ukrainian (uk)' },
  { value: 'ar', label: 'Arabic (ar)' },
  { value: 'he', label: 'Hebrew (he)' },
  { value: 'hi', label: 'Hindi (hi)' },
  { value: 'zh', label: 'Chinese (zh)' },
  { value: 'zh-CN', label: 'Chinese — Simplified (zh-CN)' },
  { value: 'zh-TW', label: 'Chinese — Traditional (zh-TW)' },
  { value: 'ja', label: 'Japanese (ja)' },
  { value: 'ko', label: 'Korean (ko)' },
  { value: 'th', label: 'Thai (th)' },
  { value: 'vi', label: 'Vietnamese (vi)' },
  { value: 'id', label: 'Indonesian (id)' },
  { value: 'ms', label: 'Malay (ms)' },
];

/**
 * Regions & ISO 3166-1 alpha-2 country options for product link context targeting.
 */
export const COUNTRY_REGION_OPTIONS: readonly SelectOptionItem[] = [
  { value: 'GLOBAL', label: 'All regions — Global (GLOBAL)' },
  { value: 'EU', label: 'European Union (EU)' },
  { value: 'GB', label: 'United Kingdom (GB)' },
  { value: 'IE', label: 'Ireland (IE)' },
  { value: 'US', label: 'United States (US)' },
  { value: 'FR', label: 'France (FR)' },
  { value: 'DE', label: 'Germany (DE)' },
  { value: 'ES', label: 'Spain (ES)' },
  { value: 'IT', label: 'Italy (IT)' },
  { value: 'NL', label: 'Netherlands (NL)' },
  { value: 'BE', label: 'Belgium (BE)' },
  { value: 'LU', label: 'Luxembourg (LU)' },
  { value: 'CH', label: 'Switzerland (CH)' },
  { value: 'AT', label: 'Austria (AT)' },
  { value: 'PT', label: 'Portugal (PT)' },
  { value: 'SE', label: 'Sweden (SE)' },
  { value: 'NO', label: 'Norway (NO)' },
  { value: 'DK', label: 'Denmark (DK)' },
  { value: 'FI', label: 'Finland (FI)' },
  { value: 'IS', label: 'Iceland (IS)' },
  { value: 'PL', label: 'Poland (PL)' },
  { value: 'CZ', label: 'Czechia (CZ)' },
  { value: 'SK', label: 'Slovakia (SK)' },
  { value: 'HU', label: 'Hungary (HU)' },
  { value: 'RO', label: 'Romania (RO)' },
  { value: 'BG', label: 'Bulgaria (BG)' },
  { value: 'GR', label: 'Greece (GR)' },
  { value: 'HR', label: 'Croatia (HR)' },
  { value: 'SI', label: 'Slovenia (SI)' },
  { value: 'EE', label: 'Estonia (EE)' },
  { value: 'LV', label: 'Latvia (LV)' },
  { value: 'LT', label: 'Lithuania (LT)' },
  { value: 'CY', label: 'Cyprus (CY)' },
  { value: 'MT', label: 'Malta (MT)' },
  { value: 'TR', label: 'Turkey (TR)' },
  { value: 'UA', label: 'Ukraine (UA)' },
  { value: 'CA', label: 'Canada (CA)' },
  { value: 'MX', label: 'Mexico (MX)' },
  { value: 'BR', label: 'Brazil (BR)' },
  { value: 'AR', label: 'Argentina (AR)' },
  { value: 'CL', label: 'Chile (CL)' },
  { value: 'CO', label: 'Colombia (CO)' },
  { value: 'AU', label: 'Australia (AU)' },
  { value: 'NZ', label: 'New Zealand (NZ)' },
  { value: 'JP', label: 'Japan (JP)' },
  { value: 'KR', label: 'South Korea (KR)' },
  { value: 'SG', label: 'Singapore (SG)' },
  { value: 'CN', label: 'China (CN)' },
  { value: 'HK', label: 'Hong Kong (HK)' },
  { value: 'TW', label: 'Taiwan (TW)' },
  { value: 'IN', label: 'India (IN)' },
  { value: 'TH', label: 'Thailand (TH)' },
  { value: 'VN', label: 'Vietnam (VN)' },
  { value: 'ID', label: 'Indonesia (ID)' },
  { value: 'MY', label: 'Malaysia (MY)' },
  { value: 'PH', label: 'Philippines (PH)' },
  { value: 'AE', label: 'United Arab Emirates (AE)' },
  { value: 'SA', label: 'Saudi Arabia (SA)' },
  { value: 'IL', label: 'Israel (IL)' },
  { value: 'ZA', label: 'South Africa (ZA)' },
];

/**
 * BCP-47 default workspace locale options for Workspace Resolver Defaults.
 */
export const WORKSPACE_LOCALE_OPTIONS: readonly SelectOptionItem[] = [
  { value: 'en-GB', label: 'English — United Kingdom (en-GB)' },
  { value: 'en-US', label: 'English — United States (en-US)' },
  { value: 'en-IE', label: 'English — Ireland (en-IE)' },
  { value: 'en-AU', label: 'English — Australia (en-AU)' },
  { value: 'en-CA', label: 'English — Canada (en-CA)' },
  { value: 'en-NZ', label: 'English — New Zealand (en-NZ)' },
  { value: 'fr-FR', label: 'French — France (fr-FR)' },
  { value: 'fr-BE', label: 'French — Belgium (fr-BE)' },
  { value: 'fr-CH', label: 'French — Switzerland (fr-CH)' },
  { value: 'fr-CA', label: 'French — Canada (fr-CA)' },
  { value: 'de-DE', label: 'German — Germany (de-DE)' },
  { value: 'de-AT', label: 'German — Austria (de-AT)' },
  { value: 'de-CH', label: 'German — Switzerland (de-CH)' },
  { value: 'es-ES', label: 'Spanish — Spain (es-ES)' },
  { value: 'es-MX', label: 'Spanish — Mexico (es-MX)' },
  { value: 'it-IT', label: 'Italian — Italy (it-IT)' },
  { value: 'nl-NL', label: 'Dutch — Netherlands (nl-NL)' },
  { value: 'nl-BE', label: 'Dutch — Belgium (nl-BE)' },
  { value: 'pt-PT', label: 'Portuguese — Portugal (pt-PT)' },
  { value: 'pt-BR', label: 'Portuguese — Brazil (pt-BR)' },
  { value: 'pl-PL', label: 'Polish — Poland (pl-PL)' },
  { value: 'sv-SE', label: 'Swedish — Sweden (sv-SE)' },
  { value: 'da-DK', label: 'Danish — Denmark (da-DK)' },
  { value: 'nb-NO', label: 'Norwegian Bokmål — Norway (nb-NO)' },
  { value: 'fi-FI', label: 'Finnish — Finland (fi-FI)' },
  { value: 'el-GR', label: 'Greek — Greece (el-GR)' },
  { value: 'cs-CZ', label: 'Czech — Czechia (cs-CZ)' },
  { value: 'hu-HU', label: 'Hungarian — Hungary (hu-HU)' },
  { value: 'ro-RO', label: 'Romanian — Romania (ro-RO)' },
  { value: 'bg-BG', label: 'Bulgarian — Bulgaria (bg-BG)' },
  { value: 'hr-HR', label: 'Croatian — Croatia (hr-HR)' },
  { value: 'sk-SK', label: 'Slovak — Slovakia (sk-SK)' },
  { value: 'sl-SI', label: 'Slovenian — Slovenia (sl-SI)' },
  { value: 'et-EE', label: 'Estonian — Estonia (et-EE)' },
  { value: 'lv-LV', label: 'Latvian — Latvia (lv-LV)' },
  { value: 'lt-LT', label: 'Lithuanian — Lithuania (lt-LT)' },
  { value: 'ga-IE', label: 'Irish — Ireland (ga-IE)' },
  { value: 'tr-TR', label: 'Turkish — Turkey (tr-TR)' },
  { value: 'ar-AE', label: 'Arabic — United Arab Emirates (ar-AE)' },
  { value: 'ar-SA', label: 'Arabic — Saudi Arabia (ar-SA)' },
  { value: 'ja-JP', label: 'Japanese — Japan (ja-JP)' },
  { value: 'ko-KR', label: 'Korean — South Korea (ko-KR)' },
  { value: 'zh-CN', label: 'Chinese — Simplified (zh-CN)' },
  { value: 'zh-TW', label: 'Chinese — Traditional (zh-TW)' },
];
